import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Typography } from '@mui/material';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import { observer } from 'mobx-react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import {
  AutoComplete,
  Breadcrumb,
  PageLoading,
  SnackbarApiErrorMessage,
  useDataProvider,
  useLocalization,
  useSnackbar,
  useUserAccess,
} from '@ui-framework/index';
import {
  ButtonContainer,
  IconLibrary,
  Layout,
  PageSection,
} from '@product-framework/index';
import {
  ISSUE_NOTIFICATION_CONFIGURATION_API_PATH,
  ISSUE_USERS_API_PATH,
} from '../../apiPaths';
import {
  createDebouncedOptionsLoader,
  getItemsFromListResult,
  getItemsFromRequiredListResult,
  mapOptionToUserId,
  mapUserIdToOption,
  mapUsersToOptions,
  shouldRefreshConfigAfterSaveError,
} from './utils';

const SEARCH_PAGE_LIMIT = 20;

function NotificationSettingsPage() {
  const { formatMessage } = useLocalization();
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const userAccess = useUserAccess();
  const issuesTrackingProvider = useDataProvider('issuesTracking');
  const isAdmin = userAccess.isAdmin();
  const companyId = userAccess?.company?.id;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [recordVersion, setRecordVersion] = useState(null);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [directoryReady, setDirectoryReady] = useState(false);
  const [directoryLoadFailed, setDirectoryLoadFailed] = useState(false);
  const configRequestId = useRef(0);
  const unavailableUserLabel = formatMessage({
    id: 'CONFIGURED_USER_UNAVAILABLE',
    defaultMessage: 'Configured user unavailable',
  });

  const { control, handleSubmit, reset, watch } = useForm({
    defaultValues: { assignmentContact: null },
  });
  const assignmentContact = watch('assignmentContact');
  const requiredAssigneeMessage = formatMessage({
    id: 'ISSUE_ASSIGNMENT_DEFAULT_ASSIGNEE_REQUIRED',
    defaultMessage: 'Select a user',
  });
  const showError = useCallback(
    errors => {
      snackbar.showMessage({
        type: 'error',
        data: <SnackbarApiErrorMessage errors={errors} />,
      });
    },
    [snackbar],
  );

  const fetchAssignableUserOptions = useCallback(
    async (
      searchValue,
      limit = SEARCH_PAGE_LIMIT,
      { required = false, userId } = {},
    ) => {
      const result = await issuesTrackingProvider.get(ISSUE_USERS_API_PATH, {
        page: 1,
        limit,
        assignableOnly: true,
        companyId,
        ...(userId && { userId }),
        ...(searchValue && { email: searchValue }),
      });
      return required
        ? getItemsFromRequiredListResult(result)
        : getItemsFromListResult(result);
    },
    [companyId, issuesTrackingProvider],
  );

  const loadConfiguration = useCallback(async () => {
    const requestId = configRequestId.current + 1;
    configRequestId.current = requestId;
    setLoading(true);
    setDirectoryReady(false);
    setDirectoryLoadFailed(false);

    try {
      const configResult = await issuesTrackingProvider.get(
        ISSUE_NOTIFICATION_CONFIGURATION_API_PATH,
      );

      if (requestId !== configRequestId.current) {
        return;
      }

      if (!configResult.ok) {
        setRecordVersion(null);
        showError(configResult?.errors);
        return;
      }

      const config = configResult.data;
      const configuredUserId = isAdmin
        ? config?.escalationUserId
        : config?.defaultAssigneeUserId;
      const configuredUsers = configuredUserId
        ? await fetchAssignableUserOptions('', 1, {
            required: true,
            userId: configuredUserId,
          })
        : [];

      if (requestId !== configRequestId.current) {
        return;
      }

      setRecordVersion(config?.recordVersion ?? null);
      setAssignableUsers(configuredUsers);
      setDirectoryReady(true);
      reset({
        assignmentContact: mapUserIdToOption(
          configuredUserId,
          configuredUsers,
          null,
          unavailableUserLabel,
        ),
      });
    } catch (error) {
      if (requestId === configRequestId.current) {
        setRecordVersion(null);
        setDirectoryLoadFailed(true);
        showError(error?.result?.errors || error?.errors);
      }
    } finally {
      if (requestId === configRequestId.current) {
        setLoading(false);
      }
    }
  }, [
    fetchAssignableUserOptions,
    isAdmin,
    issuesTrackingProvider,
    reset,
    showError,
    unavailableUserLabel,
  ]);

  useEffect(() => {
    loadConfiguration();
    return () => {
      configRequestId.current += 1;
    };
  }, [loadConfiguration]);

  const loadUserOptions = useMemo(
    () =>
      createDebouncedOptionsLoader(async searchValue =>
        mapUsersToOptions(await fetchAssignableUserOptions(searchValue)),
      ),
    [fetchAssignableUserOptions],
  );

  const onSubmit = async values => {
    if (!directoryReady) {
      return;
    }

    setSaving(true);
    try {
      const selectedUserId = mapOptionToUserId(values.assignmentContact);
      const result = await issuesTrackingProvider.put(
        ISSUE_NOTIFICATION_CONFIGURATION_API_PATH,
        {
          recordVersion,
          ...(isAdmin
            ? { escalationUserId: selectedUserId }
            : { defaultAssigneeUserId: selectedUserId }),
        },
      );

      if (result.ok) {
        setRecordVersion(result.data?.recordVersion ?? null);
        const savedUserId = isAdmin
          ? result.data?.escalationUserId
          : result.data?.defaultAssigneeUserId;
        reset({
          assignmentContact: mapUserIdToOption(
            savedUserId,
            assignableUsers,
            values.assignmentContact,
            unavailableUserLabel,
          ),
        });
        snackbar.showMessage(
          formatMessage({
            id: 'NOTIFICATION_SETTINGS_SAVED_SUCCESSFULLY',
            defaultMessage: 'Issue assignment contact was saved successfully',
          }),
        );
        return;
      }

      showError(result?.errors);
      if (shouldRefreshConfigAfterSaveError(result)) {
        await loadConfiguration();
      }
    } catch (error) {
      showError(error?.result?.errors || error?.errors);
    } finally {
      setSaving(false);
    }
  };

  const breadCrumb = [
    {
      name: formatMessage({
        id: 'DASHBOARD_BREADCRUMB',
        defaultMessage: 'Dashboard',
      }),
      icon: IconLibrary.Dashboard,
      onClick: () => navigate('/dashboard'),
    },
    {
      name: formatMessage({
        id: 'MAIN_MENU_REQUEST_AND_FEEDBACK_CONFIGURATION',
        defaultMessage: 'Configuration',
      }),
      icon: IconLibrary.Alerts,
      onClick: undefined,
    },
  ];

  if (loading) {
    return (
      <Layout>
        <PageLoading />
      </Layout>
    );
  }

  return (
    <Layout>
      <Breadcrumb items={breadCrumb} />
      <PageSection
        label={formatMessage(
          isAdmin
            ? {
                id: 'NOTIFICATIONS_ADMIN_ESCALATION_HEADING',
                defaultMessage: 'Issue Escalation Assignment Contact',
              }
            : {
                id: 'NOTIFICATIONS_ESCALATION_HEADING',
                defaultMessage: 'Issue Assignment Contact',
              },
        )}>
        <Grid2 xs={12} md={6} container direction="column" spacing={2}>
          {directoryLoadFailed && (
            <Grid2>
              <Alert severity="error">
                {formatMessage({
                  id: 'ASSIGNABLE_USERS_UNAVAILABLE',
                  defaultMessage:
                    'Assignable users could not be loaded. Try again before saving.',
                })}
              </Alert>
            </Grid2>
          )}
          <Grid2>
            <Typography variant="body2">
              {isAdmin
                ? formatMessage({
                    id: 'NOTIFICATIONS_ESCALATION_DESCRIPTION',
                    defaultMessage:
                      'Select the designated Service Xcelerator contact for escalated issues. Escalated issues are assigned to this contact.',
                  })
                : formatMessage({
                    id: 'NOTIFICATIONS_DEFAULT_ASSIGNEES_DESCRIPTION',
                    defaultMessage:
                      'Select the designated Service Administrator contact for newly reported issues. New issues are assigned to this contact.',
                  })}
            </Typography>
          </Grid2>
          <Grid2>
            <AutoComplete
              name="assignmentContact"
              control={control}
              disabled={saving || !directoryReady}
              rules={{
                validate: option =>
                  Boolean(option?.value) || requiredAssigneeMessage,
              }}
              defaultOptions
              loadOptions={loadUserOptions}
              placeholder={formatMessage(
                isAdmin
                  ? {
                      id: 'NOTIFICATIONS_ESCALATION_RECIPIENTS_PLACEHOLDER',
                      defaultMessage: 'Service Xcelerator escalation contact',
                    }
                  : {
                      id: 'NOTIFICATIONS_DEFAULT_ASSIGNEES_PLACEHOLDER',
                      defaultMessage: 'Service Administrator issue contact',
                    },
              )}
            />
          </Grid2>
          <Grid2>
            <ButtonContainer>
              <Button
                variant="contained"
                color="primary"
                startIcon={<IconLibrary.Save />}
                disabled={
                  saving ||
                  !directoryReady ||
                  !Number.isInteger(recordVersion) ||
                  !assignmentContact?.value
                }
                onClick={handleSubmit(onSubmit)}>
                {formatMessage({
                  id: 'NOTIFICATIONS_SAVE_BUTTON',
                  defaultMessage: 'Save',
                })}
              </Button>
            </ButtonContainer>
          </Grid2>
        </Grid2>
      </PageSection>
    </Layout>
  );
}

export default observer(NotificationSettingsPage);
